import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, Target, TrendingUp, TrendingDown, Edit, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { FinancePageLayout } from '@/components/finance';
import { useKpiTargets, useKpiWithStatus, type KpiTarget } from '@/hooks/finance/useKpiTargets';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const kpiSchema = z.object({
  kpi_code: z.string().min(1, 'Código é obrigatório'),
  kpi_name: z.string().min(1, 'Nome é obrigatório').max(100),
  kpi_category: z.string().optional(),
  target_value: z.coerce.number().min(0, 'Valor deve ser positivo'),
  target_type: z.enum(['min', 'max', 'exact', 'range']),
  unit: z.string().optional(),
  year: z.coerce.number().min(2020).max(2100),
  month: z.coerce.number().min(1).max(12).optional().nullable(),
  country: z.string().optional().nullable(),
});

type KpiFormData = z.infer<typeof kpiSchema>;

const formatValue = (value: number, unit: string | null | undefined) => {
  if (unit === 'USD' || unit === 'BRL') {
    return new Intl.NumberFormat(unit === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: unit,
    }).format(value);
  }
  if (unit === '%') {
    return `${value.toFixed(1)}%`;
  }
  return `${value.toLocaleString()} ${unit || ''}`.trim();
};

const CATEGORY_LABELS: Record<string, string> = {
  revenue: 'Receita',
  profit: 'Lucro',
  efficiency: 'Eficiência',
  growth: 'Crescimento',
  quality: 'Qualidade',
};

const CATEGORY_COLORS: Record<string, string> = {
  revenue: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  profit: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  efficiency: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  growth: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  quality: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
};

function useCreateKpiTarget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: KpiFormData) => {
      const insertData = {
        kpi_code: data.kpi_code,
        kpi_name: data.kpi_name,
        kpi_category: data.kpi_category,
        target_value: data.target_value,
        target_type: data.target_type,
        unit: data.unit,
        year: data.year,
        month: data.month,
        country: data.country,
        is_active: true,
      };
      const { data: result, error } = await supabase
        .from('kpi_targets')
        .insert(insertData)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpi-targets'] });
      queryClient.invalidateQueries({ queryKey: ['kpi-status'] });
    },
  });
}

function useUpdateKpiTarget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: KpiFormData & { id: string }) => {
      const { data: result, error } = await supabase
        .from('kpi_targets')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpi-targets'] });
      queryClient.invalidateQueries({ queryKey: ['kpi-status'] });
    },
  });
}

function useDeleteKpiTarget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('kpi_targets')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpi-targets'] });
      queryClient.invalidateQueries({ queryKey: ['kpi-status'] });
    },
  });
}

export default function KpiTargets() {
  const currentYear = new Date().getFullYear();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingKpi, setEditingKpi] = useState<KpiTarget | null>(null);
  const [deletingKpi, setDeletingKpi] = useState<KpiTarget | null>(null);
  
  const { data: kpiStatuses, isLoading } = useKpiWithStatus(currentYear);
  const { data: kpiTargets } = useKpiTargets(currentYear);
  
  const createKpi = useCreateKpiTarget();
  const updateKpi = useUpdateKpiTarget();
  const deleteKpi = useDeleteKpiTarget();

  const form = useForm<KpiFormData>({
    resolver: zodResolver(kpiSchema),
    defaultValues: {
      kpi_code: '',
      kpi_name: '',
      kpi_category: 'revenue',
      target_value: 0,
      target_type: 'min',
      unit: 'USD',
      year: currentYear,
      month: null,
      country: null,
    },
  });

  const handleOpenDialog = (kpi?: KpiTarget) => {
    if (kpi) {
      setEditingKpi(kpi);
      form.reset({
        kpi_code: kpi.kpi_code,
        kpi_name: kpi.kpi_name,
        kpi_category: kpi.kpi_category || 'revenue',
        target_value: kpi.target_value,
        target_type: kpi.target_type || 'min',
        unit: kpi.unit || 'USD',
        year: kpi.year,
        month: kpi.month,
        country: kpi.country,
      });
    } else {
      setEditingKpi(null);
      form.reset({
        kpi_code: '',
        kpi_name: '',
        kpi_category: 'revenue',
        target_value: 0,
        target_type: 'min',
        unit: 'USD',
        year: currentYear,
        month: null,
        country: null,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingKpi(null);
    form.reset();
  };

  const onSubmit = async (data: KpiFormData) => {
    try {
      if (editingKpi) {
        await updateKpi.mutateAsync({
          id: editingKpi.id,
          ...data,
        });
        toast.success('Meta atualizada com sucesso!');
      } else {
        await createKpi.mutateAsync(data);
        toast.success('Meta cadastrada com sucesso!');
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('Erro ao salvar meta:', error);
      toast.error(error?.message || 'Erro ao salvar meta. Verifique os dados e tente novamente.');
    }
  };

  const handleDelete = async () => {
    if (!deletingKpi) return;
    
    try {
      await deleteKpi.mutateAsync(deletingKpi.id);
      toast.success('Meta desativada com sucesso!');
      setDeletingKpi(null);
    } catch (error: any) {
      console.error('Erro ao excluir meta:', error);
      toast.error(error?.message || 'Erro ao excluir meta.');
    }
  };

  const isSubmitting = createKpi.isPending || updateKpi.isPending;

  // Calculate summary stats
  const achievedCount = kpiStatuses?.filter(k => k.status === 'ok').length || 0;
  const warningCount = kpiStatuses?.filter(k => k.status === 'warning').length || 0;
  const criticalCount = kpiStatuses?.filter(k => k.status === 'critical').length || 0;

  return (
    <FinancePageLayout>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Metas e Indicadores</h1>

        <Dialog open={isDialogOpen} onOpenChange={(open) => open ? handleOpenDialog() : handleCloseDialog()}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nova Meta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingKpi ? 'Editar Meta/KPI' : 'Adicionar Meta/KPI'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="kpi_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Código *</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: revenue" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="kpi_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome *</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: Receita Mensal" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="kpi_category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="revenue">Receita</SelectItem>
                          <SelectItem value="profit">Lucro</SelectItem>
                          <SelectItem value="efficiency">Eficiência</SelectItem>
                          <SelectItem value="growth">Crescimento</SelectItem>
                          <SelectItem value="quality">Qualidade</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="target_value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor Meta *</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="150000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unidade</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ''}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="BRL">BRL</SelectItem>
                            <SelectItem value="%">Percentual (%)</SelectItem>
                            <SelectItem value="clientes">Clientes</SelectItem>
                            <SelectItem value="dias">Dias</SelectItem>
                            <SelectItem value="leads">Leads</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="target_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="min">Mínimo (maior é melhor)</SelectItem>
                            <SelectItem value="max">Máximo (menor é melhor)</SelectItem>
                            <SelectItem value="exact">Exato</SelectItem>
                            <SelectItem value="range">Faixa</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="year"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ano *</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="month"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mês (opcional)</FormLabel>
                        <Select 
                          onValueChange={(v) => field.onChange(v ? parseInt(v) : null)} 
                          value={field.value?.toString() || ''}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Anual" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">Anual</SelectItem>
                            <SelectItem value="1">Janeiro</SelectItem>
                            <SelectItem value="2">Fevereiro</SelectItem>
                            <SelectItem value="3">Março</SelectItem>
                            <SelectItem value="4">Abril</SelectItem>
                            <SelectItem value="5">Maio</SelectItem>
                            <SelectItem value="6">Junho</SelectItem>
                            <SelectItem value="7">Julho</SelectItem>
                            <SelectItem value="8">Agosto</SelectItem>
                            <SelectItem value="9">Setembro</SelectItem>
                            <SelectItem value="10">Outubro</SelectItem>
                            <SelectItem value="11">Novembro</SelectItem>
                            <SelectItem value="12">Dezembro</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingKpi ? 'Atualizar' : 'Salvar'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Metas</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpiTargets?.length || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Metas Atingidas</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{achievedCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Alerta</CardTitle>
            <TrendingUp className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{warningCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Críticos</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* KPIs Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : kpiStatuses && kpiStatuses.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {kpiStatuses.map((kpiStatus) => {
            const { kpi, currentValue, status, percentageVsTarget } = kpiStatus;
            const progress = Math.min((currentValue / kpi.target_value) * 100, 100);
            const isHigherBetter = kpi.target_type === 'min';
            const achieved = isHigherBetter 
              ? currentValue >= kpi.target_value 
              : currentValue <= kpi.target_value;

            return (
              <Card key={kpi.id} className={cn(achieved && 'border-green-500')}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {kpi.kpi_name}
                        {achieved && <CheckCircle className="h-4 w-4 text-green-600" />}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {kpi.kpi_code}
                      </p>
                    </div>
                    <Badge className={CATEGORY_COLORS[kpi.kpi_category || 'revenue'] || CATEGORY_COLORS.revenue}>
                      {CATEGORY_LABELS[kpi.kpi_category || 'revenue'] || kpi.kpi_category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-sm text-muted-foreground">Atual</p>
                        <p className="text-2xl font-bold">
                          {formatValue(currentValue, kpi.unit)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Meta</p>
                        <p className="text-lg font-medium text-muted-foreground">
                          {formatValue(kpi.target_value, kpi.unit)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Progress 
                        value={progress} 
                        className={cn(
                          "h-2",
                          status === 'ok' ? "[&>div]:bg-green-500" : 
                          status === 'warning' ? "[&>div]:bg-yellow-500" : "[&>div]:bg-red-500"
                        )}
                      />
                      <p className={cn(
                        "text-xs text-right",
                        status === 'ok' ? "text-green-600" : 
                        status === 'warning' ? "text-yellow-600" : "text-red-600"
                      )}>
                        {percentageVsTarget.toFixed(0)}% {isHigherBetter ? 'atingido' : 'do limite'}
                      </p>
                    </div>

                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenDialog(kpi)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setDeletingKpi(kpi)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Nenhuma meta cadastrada para {currentYear}. Clique em "Nova Meta" para adicionar.
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingKpi} onOpenChange={() => setDeletingKpi(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar Meta</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desativar "{deletingKpi?.kpi_name}"? 
              Esta ação irá marcar a meta como inativa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteKpi.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Desativar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FinancePageLayout>
  );
}
